import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { AppErrorComponent } from './components/app-error/app-error.component';
import { activateAdminGuard } from './guards/activate-admin.guard';
import { canMatchForDigilogUser } from './guards/can-match.digilog.guard';
import { canMatchSap } from './guards/can-match.sap.guard';
import { LayoutComponent } from './containers/layout/layout.component';
import { SidebarLayoutComponent } from './containers/layout/sidebar-layout/sidebar-layout.component';
import { PageRedirectComponent } from './containers/page-redirect/page-redirect.component';
import { AppPage, AssetTreeViewType, SmaWebRoutes } from './models/route';
import { canMatchTenantManagement } from './guards/tenant-management.guard';
import { hasAnyLicenseGuard } from './guards/has-any-license.guard';
import { noLicenseRequiredGuard } from './guards/no-license-required.guard';
import { AuthCallbackComponent, authGuard, TenantIdGuard, WelcomeComponent } from './standalone-core/features';

export const appRoutes: SmaWebRoutes = [
  {
    path: 'service-unavailable',
    component: AppErrorComponent,
  },
  {
    path: 'auth/callback',
    component: AuthCallbackComponent,
  },
  {
    path: 'welcome',
    component: WelcomeComponent,
  },
  {
    path: 'register',
    loadComponent: () => import('./standalone-core/features/authorization/lib/components/registration/registration.component').then(m => m.RegistrationComponent),
  },
  {
    path: 'account-register',
    redirectTo: 'register',
  },
  {
    path: 'user-activate',
    loadComponent: () => import('./standalone-core/features/authorization/lib/components/user-activate/user-activate.component').then(m => m.UserActivateComponent),
  },
  {
    path: 'tenant-management',
    loadChildren: () => import('./modules/tenant-management/tenant-management.routes').then(m => m.tenantManagementRoutes),
    canMatch: [canMatchTenantManagement],
    data: { page: AppPage.TENANT_MANAGEMENT },
  },
  {
    path: '',
    canActivate: [authGuard, TenantIdGuard],
    component: LayoutComponent,
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'home',
      },
      {
        path: 'licenses',
        canActivate: [noLicenseRequiredGuard],
        loadComponent: () => import('./standalone-core/pages/licenses/licenses-smart.component').then(m => m.LicensesSmartComponent),
        data: { page: AppPage.LICENSES, state: 'licenses', pageTitle: 'NAVIGATION.LICENSES' },
      },
      {
        path: 'tree',
        outlet: 'asset',
        loadChildren: () => import('./modules/asset-tree/asset-tree.routes').then(m => m.assetTreeRoutes),
      },
      {
        path: 'home',
        canActivate: [hasAnyLicenseGuard],
        component: SidebarLayoutComponent,
        data: { page: AppPage.MY_AREA },
        loadChildren: () => import('./modules/my-space/my-space.routes').then(m => m.mySpaceRoutes),
      },
      {
        path: 'incidents',
        canActivate: [hasAnyLicenseGuard],
        data: { page: AppPage.ALL_INCIDENTS },
        loadChildren: () => import('./modules/incidents/incidents.routes').then(m => m.incidentsRoutes),
        canMatch: [canMatchForDigilogUser],
      },
      {
        path: 'export',
        canActivate: [hasAnyLicenseGuard],
        data: { page: AppPage.EXPORT },
        loadChildren: () => import('./modules/export/export.routes').then(m => m.exportRoutes),
      },
      {
        path: 'administration',
        canActivate: [activateAdminGuard, hasAnyLicenseGuard],
        canActivateChild: [activateAdminGuard],
        data: { page: AppPage.ADMINISTRATION },
        loadChildren: () => import('./modules/administration/administration.routes').then(m => m.administrationRoutes),
      },
      {
        path: 'sap/machines',
        canActivate: [hasAnyLicenseGuard],
        canMatch: [canMatchSap],
        data: { page: AppPage.SAP_MACHINES, showAsset: true, assetMode: AssetTreeViewType.NEXT_TO, redirectTo: ['/'] },
        loadChildren: () => import('./modules/sap-machines/sap-machines.routes').then(m => m.sapMachinesRoutes),
      },
      {
        path: 'machines',
        canActivate: [hasAnyLicenseGuard],
        data: { page: AppPage.MACHINES, showAsset: true, assetMode: AssetTreeViewType.NEXT_TO },
        loadChildren: () => import('./modules/machines/machines.routes').then(m => m.machinesRoutes),
      },
      {
        path: 'tenants',
        redirectTo: 'administration/licenses',
        pathMatch: 'full',
      },
      {
        path: 'spare-parts',
        canActivate: [hasAnyLicenseGuard],
        data: { page: AppPage.SPARE_PARTS },
        loadChildren: () => import('@domains/spare-parts/feature-list').then(m => m.sparePartsRoutes),
        canMatch: [canMatchForDigilogUser],
      },
      {
        path: 'm/:mid',
        redirectTo: 'machines/:mid',
      },
      {
        path: 'page-not-found',
        component: PageNotFoundComponent,
      },
      {
        path: 'page-redirect',
        component: PageRedirectComponent,
      },
    ],
  },
  { path: '**', redirectTo: 'page-not-found' },
];

